/**
 * This code is heavily influenced by the spin-delay package: https://www.npmjs.com/package/spin-delay
 * We've copied it and then modified it for our needs and with our coding conventions and attributed it below.
 *
 * MIT License
 *
 * Copyright (c) 2020 Stephan Meijer
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 */

/**
 *
 *    ┌──────────────┐____________
 *    │     IDLE     │            |
 *    └──────┬───────┘            |
 *      isLoading = true          |
 *             ▼                  |
 *    ┌──────────────┐            |
 *    │   DELAYING   │ -- isLoading = false
 *    └──────┬───────┘            |
 *      timer expires (delay)     |
 *          ▼                     |
 *    ┌──────────────┐            |
 *    │    EXPIRE    │ -- isLoading = false
 *    └──────┬───────┘            |
 *      isLoading = true          |
 *          ▼                     |
 *    ┌──────────────┐            |
 *    │   DISPLAY    │ -- isLoading = false
 *    └──────────────┘
 *           |
 *     wait for minDuration
 *           ▼
 *    ┌──────────────┐
 *    │     IDLE     │
 *    └──────────────┘
 *
 */

import * as React from 'react';

export type LoadingDelayOptions = {
  /**
   * The delay in milliseconds before the loading content (like a Spinner) is displayed.
   * @default 500
   */
  delay?: number;
  /**
   * The minimum duration in milliseconds the loading content (like a Spinner) is displayed.
   * @default 200
   */
  minDuration?: number;
};

type State = 'IDLE' | 'DELAYING' | 'DISPLAY' | 'EXPIRE';

const defaultOptions = {
  delay: 500,
  minDuration: 200,
};

export function useLoadingDelay(
  isLoading: boolean,
  options?: LoadingDelayOptions
): boolean {
  const {delay, minDuration} = React.useMemo(
    () => ({...defaultOptions, ...options}),
    [options]
  );

  const [state, setState] = React.useState<State>('IDLE');

  const delayTimerRef = React.useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined
  );
  const minDurationTimerRef = React.useRef<
    ReturnType<typeof setTimeout> | undefined
  >(undefined);

  React.useEffect(() => {
    switch (state) {
      case 'IDLE':
        if (isLoading) {
          // start the delay timer before showing the loader
          delayTimerRef.current = setTimeout(() => setState('EXPIRE'), delay);
          setState('DELAYING');
        }
        break;

      case 'DELAYING':
        if (!isLoading) {
          // loading ended before delay expired → cancel
          clearTimeout(delayTimerRef.current);
          setState('IDLE');
        }
        break;

      case 'EXPIRE':
        clearTimeout(delayTimerRef.current);
        if (isLoading) {
          // loading still true → show loader
          setState('DISPLAY');
          // enforce minimum visible duration
          minDurationTimerRef.current = setTimeout(() => {
            minDurationTimerRef.current = undefined;
          }, minDuration);
        } else {
          // loading finished before delay expired → skip loader
          setState('IDLE');
        }
        break;

      case 'DISPLAY':
        if (!isLoading) {
          // only hide if minDuration has passed
          if (minDurationTimerRef.current) {
            // delay hiding until minDuration completes
            clearTimeout(minDurationTimerRef.current);
            minDurationTimerRef.current = setTimeout(() => {
              setState('IDLE');
              minDurationTimerRef.current = undefined;
            }, minDuration);
          } else {
            setState('IDLE');
          }
        }
        break;

      default:
        break;
    }
  }, [isLoading, state, delay, minDuration]);

  // Cleanup timers on unmount
  React.useEffect(() => {
    return () => {
      clearTimeout(delayTimerRef.current);
      clearTimeout(minDurationTimerRef.current);
    };
  }, []);

  return state === 'DISPLAY';
}
